/**
 * Quick admin password reset panel — embed anywhere on an admin page.
 *
 * Uses `/api/admin/legacy/lookup` + `/api/admin/legacy/force-activate`.
 * Works for ANY user (legacy or not). Designed for production stability:
 * - Built into existing admin pages so no new routes/bundles are needed.
 * - Collapsible (default collapsed) to keep parent page tidy.
 */
import { useState } from "react";
import { Search, Loader2, KeyRound, Check, ChevronDown, AlertCircle, UserCheck } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function QuickPasswordResetPanel({ defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const token = localStorage.getItem("liztv_token") || "";
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [activating, setActivating] = useState(false);

  const search = async () => {
    if (query.trim().length < 2) {
      toast.error("최소 2자 이상 입력해주세요.");
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/legacy/lookup?q=${encodeURIComponent(query.trim())}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({ results: [] }));
      if (!res.ok) {
        // 실제 원인을 노출 (세션 만료/권한/서버 오류 구분)
        let reason = data.detail || data.message;
        if (!reason) {
          if (res.status === 401) reason = "세션이 만료되었습니다. 다시 로그인해주세요.";
          else if (res.status === 403) reason = "관리자 권한이 필요합니다.";
          else reason = `검색 실패 (오류 ${res.status})`;
        }
        throw new Error(reason);
      }
      setResults(data.results || []);
      if ((data.results || []).length === 0) toast.message("일치하는 회원이 없습니다.");
    } catch (e) {
      toast.error(e.message || "검색 실패");
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    if (!selected) return;
    if (newPassword.length < 6) { toast.error("비밀번호는 6자 이상이어야 합니다."); return; }
    setActivating(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/legacy/force-activate`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ email: selected.email, new_password: newPassword }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || data.message || "재설정 실패");
      toast.success(`✅ ${selected.username || selected.email} 비밀번호 재설정 완료`);
      setSelected(null);
      setNewPassword("");
      await search();
    } catch (e) {
      toast.error(e.message || "재설정 실패");
    } finally {
      setActivating(false);
    }
  };

  return (
    <div className="bg-gradient-to-br from-rose-50 to-amber-50 border border-rose-200 rounded-xl mb-6" data-testid="quick-pwreset-panel">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left hover:bg-white/40 rounded-xl transition-colors"
        data-testid="quick-pwreset-toggle"
      >
        <div className="flex items-center gap-2 min-w-0">
          <KeyRound className="w-5 h-5 text-rose-600 shrink-0" />
          <div className="min-w-0">
            <div className="font-bold text-stone-900">회원 비밀번호 즉시 재설정</div>
            <div className="text-xs text-stone-500 truncate">메일 발송이 안 가는 회원의 비번을 운영자가 직접 발급 (legacy/신규 무관)</div>
          </div>
        </div>
        <ChevronDown className={`w-5 h-5 text-stone-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="px-5 pb-5">
          <div className="flex gap-2 mb-3">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && search()}
              placeholder="이메일 / 닉네임 / 이름 (최소 2자)"
              className="flex-1 px-4 py-2.5 rounded-lg border border-stone-200 bg-white focus:outline-none focus:ring-2 focus:ring-rose-400 text-sm"
              data-testid="quick-pwreset-search-input"
            />
            <button
              onClick={search}
              disabled={busy}
              className="px-4 py-2.5 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold flex items-center gap-1.5 disabled:opacity-50"
              data-testid="quick-pwreset-search-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              검색
            </button>
          </div>

          {results.length > 0 && (
            <div className="space-y-1.5 mb-3 max-h-60 overflow-y-auto pr-1">
              {results.map((u) => {
                const isActive = u.legacy_account_activated || u.active;
                const isSelected = selected?.email === u.email;
                return (
                  <button
                    key={u.email}
                    onClick={() => setSelected(u)}
                    className={`w-full text-left px-3 py-2 rounded-lg border transition-colors ${
                      isSelected ? "border-rose-500 bg-white" : "border-stone-200 bg-white/60 hover:bg-white"
                    }`}
                    data-testid={`quick-pwreset-result-${u.email}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-semibold text-stone-800 text-sm truncate">{u.username || u.name || "(이름 없음)"}</div>
                        <div className="text-xs text-stone-500 truncate">{u.email}</div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {u.is_legacy && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded">LizTV</span>
                        )}
                        {isActive ? (
                          <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded">
                            <UserCheck className="w-2.5 h-2.5" /> 활성
                          </span>
                        ) : (
                          <span className="text-[10px] px-1.5 py-0.5 bg-stone-100 text-stone-600 rounded">미활성</span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {selected && (
            <div className="bg-white border border-rose-300 rounded-lg p-4">
              <div className="font-semibold text-sm text-stone-900 mb-2">
                {selected.username || selected.email} 의 임시 비밀번호 설정
              </div>
              <input
                type="text"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="임시 비밀번호 (6자 이상)"
                className="w-full px-3 py-2 rounded-lg border border-stone-200 focus:outline-none focus:ring-2 focus:ring-rose-400 text-sm mb-3"
                data-testid="quick-pwreset-tmp-password"
              />
              <div className="flex gap-2">
                <button
                  onClick={() => { setSelected(null); setNewPassword(""); }}
                  className="px-3 py-2 rounded-lg border border-stone-200 text-stone-600 text-sm hover:bg-stone-50"
                  data-testid="quick-pwreset-cancel"
                >
                  취소
                </button>
                <button
                  onClick={reset}
                  disabled={activating || newPassword.length < 6}
                  className="flex-1 px-3 py-2 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold flex items-center justify-center gap-1 disabled:bg-stone-300 disabled:cursor-not-allowed"
                  data-testid="quick-pwreset-confirm"
                >
                  {activating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  즉시 재설정
                </button>
              </div>
              <p className="text-[11px] text-stone-500 mt-2 flex items-start gap-1">
                <AlertCircle className="w-3 h-3 mt-0.5" />
                임시 비밀번호를 회원에게 안전하게 전달하세요 (카톡/전화 등).
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
